import { useEffect, useState, useRef } from 'react';
import axios, { type AxiosResponse } from 'axios';

import { useAuth } from './hooks/use-auth';
import { getTraceContext } from '@/shared/utils/trace-context';
import { matchesPathPrefixes } from './utils';
import { isE2ETestMode } from './utils/e2e';
import { mockData } from '@/mock-data';

const API_V1_PATH_PREFIXES = ['/api/v1'];

// On Vercel deployment (including custom domains), use relative paths so requests go to serverless functions
const isVercelDeployment =
  typeof window !== 'undefined' &&
  (window.location.hostname.includes('vercel.app') ||
    window.location.hostname.includes('vercel.com') ||
    window.location.hostname.includes('kognitos.com')); // Custom domain

if (isVercelDeployment) {
  // Clear any baseURL so requests use relative paths -> serverless function
  axios.defaults.baseURL = '';
  console.log(
    '[Axios] Vercel deployment detected, using relative paths for API',
  );
}

export function AxiosInterceptorProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    getAccessTokenSilently,
    logout,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuth();
  const [isReady, setIsReady] = useState(false);
  // use refs to store the interceptor ids
  const requestInterceptorIdRef = useRef<number | undefined>(undefined);
  const responseInterceptorIdRef = useRef<number | undefined>(undefined);

  // use refs to store latest auth values to avoid stale closures in interceptor
  const isAuthenticatedRef = useRef(isAuthenticated);
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);
  const logoutRef = useRef(logout);

  // Update refs whenever values change
  // eslint-disable-next-line no-restricted-syntax -- reason: Update refs to avoid stale closures in axios interceptors
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
    getAccessTokenSilentlyRef.current = getAccessTokenSilently;
    logoutRef.current = logout;
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  // eslint-disable-next-line no-restricted-syntax -- reason: Setup axios interceptor requires imperative API initialization
  useEffect(() => {
    // If interceptor already exists (React StrictMode double-render), skip
    if (requestInterceptorIdRef.current !== undefined) {
      setIsReady(true);
      return;
    }

    const setupInterceptor = () => {
      // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
      // then runtime window.__ENV__ if available)
      const isE2EMode = isE2ETestMode();

      // Also check if we're on Vercel (demo deployment) - enable demo mode automatically
      const isVercelDeployment =
        typeof window !== 'undefined' &&
        (window.location.hostname.includes('vercel.app') ||
          window.location.hostname.includes('vercel.com'));

      // Enable demo mode if E2E mode is enabled OR if we're on Vercel
      const isDemoMode = isE2EMode || isVercelDeployment;

      // Debug logging for Vercel deployment
      console.log('[Interceptor Setup]', {
        isE2EMode,
        isVercelDeployment,
        isDemoMode,
        hostname:
          typeof window !== 'undefined' ? window.location.hostname : 'SSR',
      });

      // Extract pathname from URL (handles absolute URLs with domain and query params)
      const extractPathname = (url: string): string => {
        if (!url) return '';

        try {
          // If URL is absolute (starts with http:// or https://), parse it
          if (url.startsWith('http://') || url.startsWith('https://')) {
            const urlObj = new URL(url);
            return urlObj.pathname;
          }
          // If URL is relative, strip query params and hash
          const [pathname] = url.split('?');
          const [cleanPathname] = pathname.split('#');
          // Ensure it starts with / if it's a path
          return cleanPathname.startsWith('/')
            ? cleanPathname
            : '/' + cleanPathname;
        } catch {
          // If URL parsing fails, return original URL without query params
          const [pathname] = url.split('?');
          const [cleanPathname] = pathname.split('#');
          return cleanPathname.startsWith('/')
            ? cleanPathname
            : '/' + cleanPathname;
        }
      };

      // Map API endpoints to mock data
      // This function matches URL patterns and returns the appropriate mock data
      const getMockDataForUrl = (url: string): unknown => {
        const pathname = extractPathname(url);
        const lowerPathname = pathname.toLowerCase();

        // Match patterns in order of specificity (most specific first)

        // /api/v1/me/organizations (but not /workspaces)
        if (
          lowerPathname.includes('/api/v1/me/organizations') &&
          !lowerPathname.includes('/workspaces')
        ) {
          return mockData.meOrganizations;
        }

        // /api/v1/books:latest (with or without /api/v1 prefix, handle query params in URL)
        // Check both pathname and original URL (URL might be relative like "books:latest?page_size=20")
        const lowerUrl = url.toLowerCase();
        // Match books:latest endpoint - check pathname, URL, and use regex for robustness
        if (
          lowerPathname.includes('books:latest') ||
          lowerPathname.endsWith('/books:latest') ||
          lowerPathname.match(/books:latest/i) ||
          lowerUrl.includes('books:latest') ||
          lowerUrl.match(/books:latest/i) ||
          lowerUrl.match(/\/books:latest[?&]/) // Match /books:latest? or /books:latest&
        ) {
          return mockData.booksLatest;
        }

        // booksmartset endpoint (might be a typo or different endpoint)
        if (
          lowerPathname.includes('booksmartset') ||
          lowerUrl.includes('booksmartset')
        ) {
          // Return books latest as fallback
          return mockData.booksLatest;
        }

        // /api/v1/tags
        if (
          lowerPathname.includes('/tags') &&
          !lowerPathname.includes('/tags/')
        ) {
          return mockData.tags;
        }

        // Run events endpoint: /automations/{id}/runs/{runId}/events
        // This must be checked BEFORE the runs list endpoint
        const runEventsMatch = lowerPathname.match(
          /\/automations\/([^/]+)\/runs\/([^/]+)\/events$/,
        );
        if (runEventsMatch) {
          // Return empty events structure - individual run events vary
          return { run_events: [] };
        }

        // Run detail endpoint: /automations/{id}/runs/{runId}
        const runDetailMatch = lowerPathname.match(
          /\/automations\/([^/]+)\/runs\/([^/]+)$/,
        );
        if (runDetailMatch && !lowerPathname.includes('/events')) {
          // Return a generic run structure
          return {
            name: lowerPathname.replace('/api/v1/', ''),
            state: { complete: { result: 'RUN_RESULT_COMPLETE' } },
            stage: 'AUTOMATION_STAGE_PUBLISHED',
          };
        }

        // Automation-specific runs LIST endpoint
        // Pattern: /organizations/.../automations/{automationId}/runs
        // Match any automation runs endpoint (not a specific run detail)
        const automationRunsMatch = lowerPathname.match(
          /\/automations\/([^/]+)\/runs$/,
        );
        if (automationRunsMatch) {
          // Return the mock runs data for the 4-way match automation
          return mockData.automationRuns;
        }

        // Automation run aggregates (colon-separated endpoint)
        if (
          lowerPathname.includes(':automationrunaggregates') ||
          lowerPathname.includes('/automation-run-aggregates') ||
          lowerUrl.includes(':automationrunaggregates')
        ) {
          return mockData.automationRunAggregates;
        }

        // Daily run aggregates (colon-separated endpoint)
        if (
          lowerPathname.includes(':dailyrunaggregates') ||
          lowerPathname.includes('/daily-run-aggregates') ||
          lowerUrl.includes(':dailyrunaggregates')
        ) {
          return mockData.dailyRunAggregates;
        }

        // Guide entries (workspace-level or automation-specific)
        // Handle guideEntries?filter= pattern
        if (
          lowerPathname.includes('/guideentries') ||
          lowerPathname.includes('guideentries') ||
          lowerUrl.includes('guideentries?') ||
          lowerUrl.includes('guideEntries?')
        ) {
          return mockData.guideEntries;
        }

        // Triggers (workspace-level or automation-specific)
        if (
          lowerPathname.includes('/triggers') ||
          lowerPathname.includes('triggers')
        ) {
          return mockData.triggers;
        }

        // Exception groups (check before exceptions to avoid conflicts)
        if (
          lowerPathname.includes('/exceptiongroups') ||
          lowerPathname.includes('exceptiongroups') ||
          lowerPathname.endsWith('exceptiongroups') ||
          lowerUrl.includes('exceptiongroups') ||
          lowerUrl.match(/exceptiongroups[?&]/) // Match exceptiongroups? or exceptiongroups&
        ) {
          return mockData.exceptionGroups;
        }

        // Exceptions count by group (colon-separated endpoint)
        // Check BEFORE general exceptions endpoint
        // lowerUrl is already lowercase, so only check lowercase patterns
        if (
          lowerPathname.includes(':countbygroup') ||
          lowerPathname.includes('/exceptions:countbygroup') ||
          lowerPathname.match(/exceptions:countbygroup/i) ||
          lowerUrl.includes('exceptions:countbygroup') ||
          lowerUrl.match(/exceptions:countbygroup/i)
        ) {
          return mockData.exceptionsCountByGroup;
        }

        // Exceptions count by automation (colon-separated endpoint)
        // Check BEFORE general exceptions endpoint
        // lowerUrl is already lowercase, so only check lowercase patterns
        if (
          lowerPathname.includes(':countbyautomation') ||
          lowerPathname.includes('/exceptions:countbyautomation') ||
          lowerPathname.match(/exceptions:countbyautomation/i) ||
          lowerUrl.includes('exceptions:countbyautomation') ||
          lowerUrl.match(/exceptions:countbyautomation/i)
        ) {
          return mockData.exceptionsCountByAutomation;
        }

        // Connections
        if (
          lowerPathname.includes('/connections') &&
          lowerPathname.includes('e2e-test-workspace-id')
        ) {
          return mockData.connections;
        }

        // Automations list (not a specific automation detail)
        // Handle both /automations and automations? patterns
        // Match /automations at end of pathname or automations? in URL
        const isAutomationsList =
          (lowerPathname.includes('/automations') ||
            lowerPathname.endsWith('/automations') ||
            lowerPathname.match(/\/automations$/i) ||
            lowerPathname.includes('automations') ||
            lowerUrl.includes('automations?') ||
            lowerUrl.match(/automations[?&]/i)) &&
          !lowerPathname.match(/\/automations\/[^/]+\/[^/]+/) && // Not a specific automation sub-resource like /automations/{id}/runs
          !lowerPathname.match(/\/automations\/[^/]+$/) && // Not a specific automation detail like /automations/{id}
          !lowerUrl.includes('/automations/'); // Not a specific automation detail URL

        if (isAutomationsList) {
          return mockData.automations;
        }

        // Exceptions (but not count endpoints which are handled above)
        // Match exceptions list endpoint with or without query parameters
        const isExceptionsList =
          (lowerPathname.includes('/exceptions') ||
            lowerPathname.endsWith('/exceptions') ||
            lowerUrl.includes('exceptions?') ||
            lowerUrl.match(/\/exceptions[?&]/)) && // Match /exceptions? or /exceptions&
          (lowerPathname.includes('e2e-test-workspace-id') ||
            lowerPathname.includes('/workspaces/') ||
            lowerUrl.includes('/workspaces/')) &&
          !lowerPathname.includes('/exceptions/') && // Not a specific exception detail
          !lowerUrl.includes(':countbygroup') && // Not count by group
          !lowerUrl.includes(':countbyautomation'); // Not count by automation

        if (isExceptionsList) {
          return mockData.exceptions;
        }

        // Organization detail (singular or plural, with or without /api/v1)
        // Pattern: /api/v1/organization/{id} or /api/v1/organizations/{id}
        if (
          (lowerPathname.includes('/organization/') ||
            lowerPathname.includes('/organizations/')) &&
          (lowerPathname.includes('e2e-test-org-id') ||
            lowerPathname.match(/\/organization[s]?\/[^/]+$/)) &&
          !lowerPathname.includes('/workspaces') &&
          !lowerPathname.includes('/users') &&
          !lowerPathname.includes('/connections') &&
          !lowerPathname.includes('/runs') &&
          !lowerPathname.includes(':')
        ) {
          return mockData.organization;
        }

        // Workspaces list (singular or plural organization path)
        // Pattern: /api/v1/organization/{id}/workspaces or /api/v1/organizations/{id}/workspaces
        if (
          (lowerPathname.includes('/organization/') ||
            lowerPathname.includes('/organizations/')) &&
          lowerPathname.includes('/workspaces') &&
          !lowerPathname.includes('/workspaces/') && // Not a specific workspace detail
          !lowerPathname.includes(':')
        ) {
          return mockData.workspaces;
        }

        // Workspace detail (singular or plural organization path)
        // Pattern: /api/v1/organization/{id}/workspaces/{id} or /api/v1/organizations/{id}/workspaces/{id}
        if (
          (lowerPathname.includes('/organization/') ||
            lowerPathname.includes('/organizations/')) &&
          lowerPathname.includes('/workspaces/') &&
          (lowerPathname.includes('e2e-test-workspace-id') ||
            lowerPathname.match(/\/workspaces\/[^/]+$/)) &&
          !lowerPathname.includes('/automations') &&
          !lowerPathname.includes('/exceptions') &&
          !lowerPathname.includes('/connections') &&
          !lowerPathname.includes('/drafts') &&
          !lowerPathname.includes('/users') &&
          !lowerPathname.includes('/runs') &&
          !lowerPathname.includes(':')
        ) {
          return mockData.workspace;
        }

        // Automation :query endpoint (e.g., tr11jt5jBCZsvPlEvPs7D:query)
        // Check both pathname and URL for colon-separated endpoints
        if (
          lowerPathname.includes(':query') ||
          lowerPathname.match(/:query/i) ||
          lowerUrl.includes(':query') ||
          lowerUrl.match(/:query/i) ||
          (lowerPathname.includes('/automations/') &&
            (lowerPathname.includes('tr11jt5jbcZsvplevps7d') ||
              lowerUrl.includes('tr11jt5jbcZsvplevps7d')) &&
            (lowerPathname.includes('query') ||
              lowerPathname.includes(':query') ||
              lowerUrl.includes(':query')))
        ) {
          return mockData.automationQuery;
        }

        // Action run aggregates (singular organization path)
        if (
          lowerPathname.includes(':actionrunaggregates') ||
          lowerPathname.includes('/action-run-aggregates')
        ) {
          return mockData.automationRunAggregates;
        }

        // Runs endpoint (workspace-level or organization-level, not automation-specific)
        // Pattern: /runs?page_size=... or /organization/{id}/runs or just runs?...
        // Handle both absolute paths (/runs) and relative URLs (runs?)
        // Check for runs endpoint but exclude automation-specific runs (handled above)
        const isRunsEndpoint =
          lowerPathname.includes('/runs') ||
          lowerPathname === '/runs' ||
          lowerPathname.endsWith('/runs') ||
          lowerPathname.includes('runs') ||
          lowerUrl.includes('runs?') ||
          lowerUrl.match(/^runs\?/) || // Matches "runs?" at start of URL
          lowerUrl.match(/\/runs\?/) || // Matches "/runs?" anywhere in URL
          lowerUrl.match(/runs[?&]/); // Matches "runs?" or "runs&" anywhere in URL

        const isAutomationSpecific =
          lowerPathname.includes('/automations/') ||
          lowerUrl.includes('/automations/') ||
          lowerPathname.includes('/automations') ||
          lowerUrl.includes('/automations');

        if (isRunsEndpoint && !isAutomationSpecific) {
          // Return automation runs as fallback (workspace-level runs might not have separate mock)
          return mockData.automationRuns;
        }

        // Users endpoint
        if (
          lowerPathname.includes('/users') &&
          (lowerPathname.includes('e2e-test-org-id') ||
            lowerPathname.includes('e2e-test-workspace-id'))
        ) {
          // Return empty users list
          return { users: [] };
        }

        return null;
      };

      /**
       * Get mock response for POST requests.
       * Handles thread creation, message sending, automation creation, etc.
       */
      const getMockPostResponse = (url: string, pathname: string): unknown => {
        const lowerUrl = url.toLowerCase();
        const lowerPathname = pathname.toLowerCase();
        const timestamp = new Date().toISOString();

        // Create thread: POST /agents/quill/threads
        if (
          lowerPathname.includes('/agents/quill/threads') &&
          !lowerUrl.includes(':')
        ) {
          const threadId = 'demo-thread-' + Date.now();
          return {
            name: `organizations/e2e-test-org-id/workspaces/e2e-test-workspace-id/agents/quill/threads/${threadId}`,
            create_time: timestamp,
            update_time: timestamp,
            state: 'THREAD_STATE_ACTIVE',
            automation: '',
          };
        }

        // Send message: POST /agents/quill/threads/{id}:sendMessage
        if (lowerUrl.includes(':sendmessage')) {
          const threadMatch = pathname.match(/threads\/([^/:]+):sendMessage/i);
          const threadId = threadMatch ? threadMatch[1] : 'demo-thread';
          const threadName = `organizations/e2e-test-org-id/workspaces/e2e-test-workspace-id/agents/quill/threads/${threadId}`;

          return {
            name: `${threadName}/messages/demo-msg-${Date.now()}`,
            state: 'STATE_COMPLETE',
            create_time: timestamp,
            agent_message: {
              content:
                "I understand you'd like to create an automation. This is a demo environment.",
            },
            completion_response: {},
          };
        }

        // Resume stream: POST /threads/{id}:resumeStream
        if (lowerUrl.includes(':resumestream')) {
          return { messages: [] };
        }

        // Create automation: POST /automations (but not :query or :invoke)
        if (lowerPathname.match(/\/automations$/) && !lowerUrl.includes(':')) {
          const automationId = '8SYj7Nu5ohpb9JaIeVwGg';
          return {
            name: `organizations/e2e-test-org-id/workspaces/e2e-test-workspace-id/automations/${automationId}`,
            display_name: 'SAP Invoice Processing',
            description: 'Demo automation',
            stage: 'AUTOMATION_STAGE_DRAFT',
            create_time: timestamp,
            update_time: timestamp,
            version: '0.1',
          };
        }

        // Invoke automation: POST /automations/{id}:invoke
        if (lowerUrl.includes(':invoke')) {
          const runId = 'mock-run-' + Date.now();
          const automationMatch = pathname.match(
            /\/automations\/([^/:]+):invoke/i,
          );
          const automationId = automationMatch ? automationMatch[1] : 'unknown';

          return {
            run_id: `organizations/e2e-test-org-id/workspaces/e2e-test-workspace-id/automations/${automationId}/runs/${runId}`,
          };
        }

        // Default: return empty object for other POST requests
        return {};
      };

      // Get empty response structure for unmatched API endpoints
      // This prevents 404s by returning appropriate empty data structures
      const getEmptyResponseForPath = (
        pathname: string,
        url: string,
      ): unknown => {
        const lowerPathname = pathname.toLowerCase();
        const lowerUrl = url.toLowerCase();

        // Exception groups - return empty group_counts structure
        if (
          lowerPathname.includes('exceptiongroups') ||
          lowerUrl.includes('exceptiongroups')
        ) {
          return { group_counts: [] };
        }

        // Exception count endpoints - return empty counts
        // lowerUrl is already lowercase, so only check lowercase patterns
        if (
          lowerUrl.includes(':countbygroup') ||
          lowerUrl.includes(':countbyautomation')
        ) {
          return { group_counts: [], automation_counts: [] };
        }

        // Detail endpoints - return empty object (match patterns like /automations/{id}, /exceptions/{id})
        // Check for detail patterns BEFORE list patterns
        const isDetailEndpoint =
          lowerPathname.match(/\/automations\/[^/]+$/) || // /automations/{id}
          lowerPathname.match(/\/exceptions\/[^/]+$/) || // /exceptions/{id}
          lowerPathname.match(/\/runs\/[^/]+$/) || // /runs/{id}
          lowerPathname.match(/\/users\/[^/]+$/) || // /users/{id}
          lowerPathname.match(/\/connections\/[^/]+$/); // /connections/{id}

        if (isDetailEndpoint) {
          // Return empty object for detail endpoints
          return {};
        }

        // List endpoints - return empty array
        if (
          lowerPathname.includes('/users') ||
          lowerPathname.includes('/runs') ||
          lowerPathname.includes('/automations') ||
          lowerPathname.includes('/exceptions') ||
          lowerPathname.includes('/connections') ||
          lowerPathname.includes('/triggers') ||
          lowerPathname.includes('/guideentries') ||
          lowerUrl.includes('users?') ||
          lowerUrl.includes('runs?') ||
          lowerUrl.includes('automations?') ||
          lowerUrl.includes('exceptions?') ||
          lowerUrl.includes('connections?') ||
          lowerUrl.includes('triggers?') ||
          lowerUrl.includes('guideentries?') ||
          lowerUrl.includes('guideEntries?')
        ) {
          return { data: [], items: [], results: [] };
        }

        // Books/booksets endpoints - return empty books structure
        if (
          lowerPathname.includes('/books') ||
          lowerPathname.includes('booksmartset') ||
          lowerUrl.includes('books') ||
          lowerUrl.includes('booksmartset')
        ) {
          return { books: [], items: [] };
        }

        // Organization/workspace detail endpoints - return minimal structure
        if (
          lowerPathname.includes('/organization') ||
          lowerPathname.includes('/workspace')
        ) {
          return {};
        }

        // Default: return empty object
        return {};
      };

      // Setup request interceptor - read from refs to avoid stale closure
      const requestInterceptor = axios.interceptors.request.use(
        async config => {
          // E2E test mode - add test header and skip auth
          if (isE2EMode) {
            config.headers['X-E2E-Test-Mode'] = 'true';
            return config;
          }

          // Read latest values from refs instead of closure
          const currentIsAuthenticated = isAuthenticatedRef.current;
          const currentGetAccessTokenSilently =
            getAccessTokenSilentlyRef.current;
          const currentLogout = logoutRef.current;

          // Production mode - add request ID header and auth token for API requests
          const traceContext = getTraceContext();
          config.headers['x-kog-request-id'] = traceContext.requestId;

          // Only try to get token if authenticated
          if (currentIsAuthenticated) {
            try {
              // Always try to get token - Auth0 will handle auth state internally
              const token = await currentGetAccessTokenSilently();
              config.headers.Authorization = `Bearer ${token}`;
            } catch (error) {
              if (error instanceof Error) {
                if (error.message.includes('Missing Refresh Token')) {
                  currentLogout();
                }
              }
              // Reject the request if we can't get the token
              return Promise.reject(
                new Error('Failed to get access token for API request'),
              );
            }
          }

          return config;
        },
        error => Promise.reject(error),
        {
          runWhen: config => matchesPathPrefixes(config, API_V1_PATH_PREFIXES),
        },
      );

      // Setup response interceptor to handle 404s in demo mode
      const responseInterceptor = axios.interceptors.response.use(
        response => response,
        error => {
          // Debug: Log all errors to understand what's happening
          console.log('[Interceptor Error]', {
            status: error?.response?.status,
            hasConfig: !!error.config,
            url: error?.config?.url,
            baseURL: error?.config?.baseURL,
            isDemoMode,
          });

          // In demo mode, intercept 404 errors and return mock data
          if (error?.response?.status === 404 && error.config) {
            // Construct full URL (handles axios baseURL)
            const baseURL = error.config.baseURL || '';
            const url = error.config.url || '';
            // If baseURL exists and url is relative, combine them
            const fullUrl =
              baseURL && !url.startsWith('http')
                ? `${baseURL.replace(/\/$/, '')}${url.startsWith('/') ? url : '/' + url}`
                : url;

            const pathname = extractPathname(fullUrl);
            const lowerUrl = fullUrl.toLowerCase();

            // Check if this is an API request
            // Primary check: Use matchesPathPrefixes with the full URL
            // Create a config object with the full URL for matching
            const configForMatching = { ...error.config, url: fullUrl };
            const isApiRequest =
              matchesPathPrefixes(configForMatching, API_V1_PATH_PREFIXES) ||
              pathname.includes('/api/v1') ||
              lowerUrl.includes('/api/v1') ||
              // Edge cases: Colon-separated endpoints that might not have /api/v1 prefix
              // lowerUrl is already lowercase, so only check lowercase patterns
              lowerUrl.includes('books:latest') ||
              lowerUrl.includes(':query') ||
              lowerUrl.includes(':countbygroup') ||
              lowerUrl.includes(':countbyautomation') ||
              lowerUrl.includes(':automationrunaggregates') ||
              lowerUrl.includes(':dailyrunaggregates') ||
              lowerUrl.includes(':actionrunaggregates');

            console.log('[Interceptor 404]', {
              fullUrl,
              pathname,
              isApiRequest,
              isDemoMode,
            });

            // Only intercept if in demo mode AND it's an API request
            if (isDemoMode && isApiRequest) {
              // Handle POST requests with mock responses
              if (error.config.method?.toLowerCase() === 'post') {
                const postResponse = getMockPostResponse(fullUrl, pathname);
                console.log('[Interceptor POST]', {
                  fullUrl,
                  hasResponse: postResponse !== null,
                });
                if (postResponse !== null) {
                  return Promise.resolve({
                    data: postResponse,
                    status: 200,
                    statusText: 'OK',
                    headers: {},
                    config: error.config,
                  } as AxiosResponse);
                }
              }

              const mockDataForUrl = getMockDataForUrl(fullUrl);
              console.log('[Interceptor Mock]', {
                fullUrl,
                hasMockData: mockDataForUrl !== null,
              });

              if (mockDataForUrl !== null) {
                // Return mock data as successful response
                return Promise.resolve({
                  data: mockDataForUrl,
                  status: 200,
                  statusText: 'OK',
                  headers: {},
                  config: error.config,
                } as AxiosResponse);
              }

              // Catch-all: Return empty data structure for unmatched API endpoints
              // This prevents 404 errors and allows the app to continue functioning
              const emptyResponse = getEmptyResponseForPath(pathname, fullUrl);
              console.log('[Interceptor Empty]', {
                fullUrl,
                emptyResponse,
              });

              return Promise.resolve({
                data: emptyResponse,
                status: 200,
                statusText: 'OK',
                headers: {},
                config: error.config,
              } as AxiosResponse);
            }
          }
          return Promise.reject(error);
        },
      );

      return { request: requestInterceptor, response: responseInterceptor };
    };

    const interceptors = setupInterceptor();
    requestInterceptorIdRef.current = interceptors.request;
    responseInterceptorIdRef.current = interceptors.response;
    setIsReady(true);
  }, []);

  if (isAuthLoading || !isReady) {
    return null;
  }

  return <>{children}</>;
}
